import React, { useState } from "react";
import { toast } from "sonner";
import { Inbox, Send } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, Select, Textarea, Input, Field, EmptyState, ModalShell } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/vda";

const SRC_LABEL = { one_link: "1Link", technical_vor: "Vehicle Off Road", daily_hire: "Daily hire (Thrifty)", i247_ooh: "OOH · i247", generic_email: "Email", fw_f12: "FW F12" };
const STATUS_TONE = { parsed: "clay", matched: "green", applied: "green", exception: "amber", failed: "red", received: "muted" };

export default function Ingestion() {
  const { data, loading, reload } = useAsync(() => api.ingestionList(), []);
  const [tab, setTab] = useState("queue");
  const [form, setForm] = useState({ from_address: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);

  if (loading) return <StaffShell><Spinner /></StaffShell>;
  const messages = data?.messages || [];

  const intake = async () => {
    if (!form.body.trim()) { toast.error("Paste the email body."); return; }
    setBusy(true);
    try {
      const r = await api.ingestionIntake(form);
      toast.success(r.duplicate ? "Already ingested (idempotent)." : `Parsed as ${SRC_LABEL[r.source] || r.source} · ${r.match_result}.`);
      setForm({ from_address: "", subject: "", body: "" }); setTab("queue"); reload();
    } catch (e) { toast.error(e.message || "Could not parse."); }
    finally { setBusy(false); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setSel(null); reload(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Inbound ingestion</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">Email ingestion review</h1>
      <p className="text-sm text-muted-foreground mb-5">Parsed 1Link, Vehicle Off Road and daily-hire messages. Nothing is applied automatically — review and apply, or reject to the exception queue.</p>

      <div className="flex gap-2 mb-5">
        {[["queue", "Review queue"], ["intake", "Add message"]].map(([k, l]) => (
          <button key={k} data-testid={`ingest-tab-${k}`} onClick={() => setTab(k)}
            className={`h-9 px-4 text-sm rounded-[6px] border ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))]"}`}>{l}</button>
        ))}
      </div>

      {tab === "intake" && (
        <Panel className="p-5 space-y-3 max-w-2xl" data-testid="ingest-intake">
          <Field label="From address"><Input data-testid="ingest-from" value={form.from_address} onChange={(e) => setForm((f) => ({ ...f, from_address: e.target.value }))} placeholder="DoNotReply@venson.com" /></Field>
          <Field label="Subject"><Input data-testid="ingest-subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} /></Field>
          <Field label="Email body"><Textarea data-testid="ingest-body" rows={10} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className="font-mono text-xs" /></Field>
          <Btn data-testid="ingest-submit" onClick={intake} loading={busy}><Send className="h-4 w-4" /> Parse &amp; queue</Btn>
        </Panel>
      )}

      {tab === "queue" && (messages.length === 0 ? <EmptyState title="Nothing ingested yet" hint="Add a message to see the parser in action." /> : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Source</th><th className="px-4 py-2.5 font-medium">Subject</th>
                <th className="px-4 py-2.5 font-medium">Match</th><th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Received</th><th className="px-4 py-2.5"></th>
              </tr></thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id} className="border-t border-border hover:bg-secondary/40" data-testid={`ingest-row-${m.id}`}>
                    <td className="px-4 py-2.5"><Tag tone="clay">{SRC_LABEL[m.source] || m.source}</Tag></td>
                    <td className="px-4 py-2.5 max-w-[280px] truncate">{m.subject || (m.parsed_data?.registration || "—")}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{m.match_result}{m.exception_reason ? ` · ${m.exception_reason}` : ""}</td>
                    <td className="px-4 py-2.5"><Tag tone={STATUS_TONE[m.parse_status] || "muted"}>{m.parse_status}</Tag></td>
                    <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">{fmtDateTime(m.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {m.parse_status !== "applied" && <Btn variant="outline" className="h-8" data-testid={`ingest-open-${m.id}`} onClick={() => setSel(m)}>Review</Btn>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}

      {sel && (
        <ModalShell onClose={() => setSel(null)} className="p-5" testId="ingest-detail-shell">
          <div data-testid="ingest-detail">
            <h3 className="font-semibold flex items-center gap-2"><Inbox className="h-4 w-4" /> {SRC_LABEL[sel.source] || sel.source} · {sel.parsed_data?.event_type}</h3>
            <div className="mt-3 text-sm space-y-1">
              {Object.entries(sel.parsed_data || {}).filter(([k, v]) => v && !["event_type", "normalized_registration", "public_summary"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex gap-2"><span className="text-muted-foreground w-40 shrink-0">{k.replace(/_/g, " ")}</span><span className="break-words">{String(v)}</span></div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Match: {sel.match_result}{sel.exception_reason ? ` — ${sel.exception_reason}` : ""}</p>
            <details className="mt-2"><summary className="text-xs text-[hsl(var(--vda-quiet))] cursor-pointer">Raw email</summary>
              <pre className="text-xs mt-1 whitespace-pre-wrap break-words font-mono bg-secondary/40 p-2 rounded-[6px]">{sel.raw_body}</pre></details>
            <div className="flex gap-2 mt-4">
              <Btn variant="outline" onClick={() => setSel(null)}>Close</Btn>
              <Btn variant="danger" data-testid="ingest-reject" onClick={() => act(() => api.ingestionReject(sel.id, "Reviewed — not applied"), "Moved to exceptions.")}>Reject</Btn>
              <Btn data-testid="ingest-apply" onClick={() => act(() => api.ingestionApply(sel.id), "Applied to booking + history.")} loading={busy}>Apply</Btn>
            </div>
          </div>
        </ModalShell>
      )}
    </StaffShell>
  );
}
